// Phase 3M — shared retail customer helpers extracted from RetailCustomer.jsx.
// Behaviour MUST stay identical to the prior inline definitions; any change
// here will leak into every retail surface.

export function formatMoney(amount, currency) {
  if (amount == null) return "";
  const symbol = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  const fixed = Number(amount).toFixed(2);
  return symbol ? `${symbol}${fixed}` : `${fixed} ${currency || ""}`.trim();
}

// Strip Wix's `/v1/fill/.../blur_N,.../` transformation pipeline so the
// browser fetches the original-quality asset. Falls through unchanged for
// non-Wix URLs. Also a generic safety-net for other CDNs that embed
// `blur_N` query/path tokens.
export function cleanImageUrl(url) {
  if (typeof url !== "string" || !url) return url;
  const wix = url.match(/^(https?:\/\/static\.wixstatic\.com\/media\/[^/]+)\/v1\/[^/]+\/[^/]+\/(.+)$/);
  if (wix) return wix[1];
  return url.replace(/[,/]blur_\d+(,|\/)/, (_, sep) => (sep === "/" ? "/" : ","));
}

// Tenant-logo / backend-media URL resolution (Sep 2026) — confirmed
// storefront defect: the tenant logo rendered correctly inside the
// Business Profile but was broken/missing on the Sales Order welcome
// screen, the storefront header and the retail chat header.
//
// ROOT CAUSE (single source, no new field): `routes/branding.py` stores
// the logo as a BACKEND-RELATIVE path — `/api/branding/files/logos/
// {company_id}/{uuid}.{ext}` — served by the API's own StaticFiles
// mount. Every dashboard surface already prefixed the API origin
// (`LogoUploader.jsx`, `Dashboard.jsx`, `chat/ChatHeader.jsx`), but the
// storefront surfaces rendered `cleanImageUrl(tenant.logo_url)` raw. On
// a deployment where the storefront and the API are different origins
// (the live setup), the browser resolved `/api/branding/files/...`
// against the STOREFRONT origin and got a 404 — a broken image.
//
// This resolves a stored media reference to something a browser can
// actually fetch, and is deliberately the ONLY place that knows the
// rule:
//   * absolute (http/https/protocol-relative), `data:` and `blob:` URLs
//     are returned untouched — an externally hosted logo keeps working;
//   * a backend-relative path is prefixed with the API origin;
//   * anything else (including a site-relative asset under `/static`)
//     is returned untouched.
// It never widens access: the path served is exactly the one the
// backend already publishes, through the same public StaticFiles mount.
const _ABSOLUTE_URL = /^(https?:)?\/\//i;

export function resolveMediaUrl(url) {
  if (typeof url !== "string") return url;
  const raw = url.trim();
  if (!raw) return "";
  if (_ABSOLUTE_URL.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) {
    return raw;
  }
  if (raw.startsWith("/api/")) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    return base ? `${base}${raw}` : raw;
  }
  return raw;
}

// Convenience: the storefront always wants BOTH the CDN clean-up and the
// origin resolution, in that order, and never one without the other.
export function resolveTenantLogoUrl(url) {
  const cleaned = cleanImageUrl(url);
  return resolveMediaUrl(typeof cleaned === "string" ? cleaned : "");
}

// Cart-item field-name adapters — cart API returns `product_name` + `unit_price`
// while the public catalog returns `name` + `price`.
export const itName  = (it) => it?.product_name ?? it?.name ?? "Item";
export const itPrice = (it) => Number(it?.unit_price ?? it?.price ?? 0);
export const itLineTotal = (it) => Number(it?.line_total ?? itPrice(it) * (it?.quantity || 0));

// Compact dietary badges. Reads ONLY explicit structured fields on the
// product — never inferred from name/description.
// Supported sources: `dietary` (string or array), `is_vegetarian` /
// `is_vegan` / `is_gluten_free` booleans, or values inside `custom_fields`
// / `extra` (case-insensitive keys).
export function dietaryBadgesFor(product) {
  const badges = [];
  const seen = new Set();
  const push = (k, title, cls) => {
    if (seen.has(k)) return;
    seen.add(k);
    badges.push({ k, title, cls });
  };
  const allFields = { ...(product || {}), ...(product?.custom_fields || {}), ...(product?.extra || {}) };
  const lower = {};
  for (const [k, v] of Object.entries(allFields || {})) lower[String(k).toLowerCase()] = v;
  const dietaryRaw = lower.dietary || lower.dietary_type || allFields.dietary || allFields.dietary_type;
  let dietary = [];
  if (Array.isArray(dietaryRaw)) dietary = dietaryRaw.map((v) => String(v).toLowerCase());
  else if (typeof dietaryRaw === "string") dietary = dietaryRaw.toLowerCase().split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
  const flag = (k) => lower[k] === true || lower[k] === "true" || lower[k] === 1;
  // Product attributes (Sep 2026) — the Vegan badge reads the word
  // "VEGAN" in full rather than the "VG" shorthand it used to. A
  // two-letter code plus a `title` tooltip relies on hover, which a
  // touch customer on a phone or the Samsung tablet kiosk never gets;
  // the badge must be understandable from its text alone, and never
  // from its green colour alone.
  if (flag("is_vegan") || dietary.includes("vegan")) push("VEGAN", "Vegan", "bg-emerald-100 text-emerald-700 border border-emerald-200");
  if (flag("is_vegetarian") || dietary.includes("vegetarian") || dietary.includes("veg")) push("V", "Vegetarian", "bg-emerald-100 text-emerald-700 border border-emerald-200");
  if (flag("is_gluten_free") || dietary.includes("gluten-free") || dietary.includes("gf") || dietary.includes("gluten free")) push("GF", "Gluten-free", "bg-amber-100 text-amber-800 border border-amber-200");
  return badges;
}

// ---------------------------------------------------------------------------
// Product attributes (Sep 2026) — Brand / Package Size / Unit / Spice.
// ---------------------------------------------------------------------------

// Product Owner wording is Low / Medium / Hot. The backend stores the
// canonical codes and also accepts the legacy "Mild" as an input alias
// for `low` (see backend `retail/product_attributes.py`); this map is
// display only and never rewrites stored data.
export const SPICE_LABELS = { low: "Low", medium: "Medium", hot: "Hot" };
export const SPICE_SHORT = { low: "L", medium: "M", hot: "H" };

// The backend already derives `pack_label` in ONE place and sends it on
// every card (`retail/product_attributes.py::format_pack_label`), so
// the storefront and a printed receipt can never disagree. This is the
// fallback for a payload that predates that field — same rules,
// including suppressing the uninformative "1 each".
export function formatPackLabel(product) {
  if (!product) return "";
  if (product.pack_label) return product.pack_label;
  const value = product.package_size_value;
  const unit = (product.unit_of_measure || "").trim();
  const label = (product.package_size_label || "").trim();
  const parts = [];
  if (value !== null && value !== undefined && value !== "" && unit) {
    const n = Number(value);
    if (!Number.isNaN(n) && !(n === 1 && unit.toLowerCase() === "each")) {
      parts.push(`${n} ${unit}`);
    }
  } else if (!value && unit && unit.toLowerCase() !== "each") {
    parts.push(unit);
  }
  if (label) parts.push(label);
  return parts.join(" · ");
}

// The compact spice indicator. Returns `null` when the product has no
// spice level, so a card renders nothing rather than an empty chip.
// Always carries readable TEXT — never colour alone.
export function spiceIndicatorFor(product) {
  const code = String(product?.spice_level || "").toLowerCase();
  if (!SPICE_LABELS[code]) return null;
  const tone = {
    low: "bg-amber-50 text-amber-700 border-amber-200",
    medium: "bg-orange-100 text-orange-800 border-orange-300",
    hot: "bg-rose-100 text-rose-800 border-rose-300",
  }[code];
  return {
    code,
    short: SPICE_SHORT[code],
    label: SPICE_LABELS[code],
    cls: tone,
  };
}

// The customer's own selection on ONE cart/order line (Sep 2026,
// restored). Mirrors `backend/retail/product_attributes.py::
// line_spice_fields` — the server sends `spice_level` on the line and
// `spice_level_label` on an order item, and this is the one place the
// storefront turns either into the compact "M · Medium" a cart row
// shows. Returns `null` for a line with no selection, so a product that
// has no spice level displays nothing anywhere.
export function lineSpiceFor(line) {
  const code = String(line?.spice_level || "").toLowerCase();
  if (!SPICE_LABELS[code]) return null;
  return { code, short: SPICE_SHORT[code], label: SPICE_LABELS[code] };
}
